import { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { firebaseAuth } from '@/lib/firebase';
import {
  CANCELLED_POPUP_CODES,
  firebaseErrorCode,
  firebaseErrorMessage,
  isApiError,
} from '@react-apps/auth';
import { extractApiErrorCode } from '@/lib/api-client';
import { authContainer } from '../../di/container';
import { useAuthStore } from '../store/auth.store';
import type { SignInInput } from '../../domain/entities/auth.entity';
import { extractRoles } from '../../domain/entities/auth.entity';

/**
 * Cópias deste app para os códigos de recusa — o backend só manda o código.
 *
 * `ACCOUNT_NOT_FOUND` (404, do `sign-in`) e `REGISTRATION_REQUIRED` (403, da
 * entrada federada) são o mesmo fato para quem lê a tela: não há conta. O
 * segundo é o que este app recebe hoje; o primeiro fica porque outros fluxos
 * ainda o devolvem.
 *
 * `PLATFORM_PROFILE_REQUIRED` é diferente e merece texto próprio: a conta
 * existe, o que falta é acesso a ESTE painel. Mandar "e-mail não cadastrado"
 * nesse caso faria a pessoa procurar o problema no lugar errado.
 *
 * `PASSWORD_CHANGE_REQUIRED` tem dois desfechos, e é de propósito. No caminho
 * com senha, o `signIn` intercepta antes de chegar aqui e leva a temporária
 * para a tela de troca (`/change-password-required`), que reautentica com ela.
 * Nos outros caminhos (token, Google, reload com sessão viva) não existe
 * temporária em mão para reautenticar, então a recusa cai aqui: a sessão é
 * derrubada — ela não serve a este passo — e a mensagem pede a entrada por
 * e-mail + temporária, a única que resolve. Mandar esses caminhos para a tela
 * de troca criava um LAÇO: sem `tempPassword` ela reencaminhava ao `/sign-in`,
 * cuja entrada automática (`onAuthStateChanged`) batia no mesmo 403.
 */
const REFUSAL_MESSAGES: Record<string, string> = {
  ACCOUNT_NOT_FOUND:
    'Este email não está cadastrado em nosso sistema, entre em contato com um administrador',
  REGISTRATION_REQUIRED:
    'Este email não está cadastrado em nosso sistema, entre em contato com um administrador',
  PLATFORM_PROFILE_REQUIRED:
    'Sua conta existe, mas não tem acesso ao painel administrativo. Entre em contato com um administrador.',
  ACCOUNT_NOT_ACTIVATED:
    'Sua conta ainda não foi ativada. Abra o link que enviamos por e-mail para definir sua senha — se ele expirou, peça um novo convite a um administrador.',
  PASSWORD_CHANGE_REQUIRED:
    'Sua senha é temporária e precisa ser trocada. Entre com seu e-mail e a senha temporária para definir uma nova senha.',
};

/**
 * Códigos em que o fluxo CONTINUA em cima da sessão Firebase recém-criada —
 * derrubá-la aqui tiraria da pessoa a sessão de que o passo seguinte depende.
 * Este painel não tem tela para o muro de verificação (mora no Painel de
 * Conta), então a saída é o link do `errorAction`.
 *
 * `PASSWORD_CHANGE_REQUIRED` **não** entra aqui, e a ausência é carga útil:
 * quando a recusa chega a `handleError` é porque não temos a senha temporária,
 * e manter a sessão nesse caso é exatamente o que realimentava a entrada
 * automática da `SignInPage`.
 */
const KEEPS_SESSION = new Set(['ACCOUNT_NOT_ACTIVATED']);

/** Recusas que significam "não existe conta": o uid recém-criado é órfão. */
const NO_ACCOUNT_CODES = new Set([
  'ACCOUNT_NOT_FOUND',
  'REGISTRATION_REQUIRED',
]);

/**
 * O uid nasceu NESTE clique? Só então apagá-lo é seguro.
 *
 * Com "vincular contas que usam o mesmo e-mail" (o `allow_duplicate_emails =
 * false`, confirmado em `primum-core-dev` em 2026-09-03), o popup do Google num
 * e-mail que já tinha conta por senha **não** cria uid novo nem falha: entra no
 * uid existente e anexa o provedor. Apagar esse uid destruiria a credencial de
 * senha de alguém por causa de um estado nosso.
 *
 * O uid que o popup acabou de criar tem exatamente um provedor, e é o do
 * popup. É higiene do console, não defesa: quem ataca controla o client, e quem
 * fecha o uid forjado é o provisionamento no back.
 */
const FEDERATED_PROVIDER_IDS = new Set<string>(['google.com', 'microsoft.com']);

function isFreshFederatedUid(user: { providerData: { providerId: string }[] }) {
  return (
    user.providerData.length === 1 &&
    FEDERATED_PROVIDER_IDS.has(user.providerData[0]?.providerId ?? '')
  );
}

/** Provedores com botão neste painel. */
export type FederatedMethod = 'google.com' | 'microsoft.com';

/**
 * Microsoft num e-mail que já tem uid (conta por senha, ou nascida de compra sem
 * primeiro acesso): o Firebase não a anexa sozinho, como faz com o Google, e
 * recusa no client antes de criar sessão. Nada a apagar nem a derrubar — a
 * saída é entrar como já entra e vincular a Microsoft no Painel de Conta.
 */
const ACCOUNT_EXISTS_CODE = 'auth/account-exists-with-different-credential';
const ACCOUNT_EXISTS_MESSAGE =
  'Já existe uma conta com este e-mail. Entre com sua senha — ou pelo primeiro acesso, se nunca entrou — e vincule a Microsoft em Segurança da conta, no Painel de Conta. É uma vez só.';

/** Onde a pessoa resolve o que este painel não resolve (muro, senha temporária). */
function accountPanelUrl(): string | null {
  const url = import.meta.env.VITE_USER_DASHBOARD_URL as string | undefined;
  return url ?? null;
}

/** Link de saída oferecido junto do erro, quando existe um lugar para onde ir. */
export interface ErrorAction {
  label: string;
  href: string;
}

export function useSignIn() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorAction, setErrorAction] = useState<ErrorAction | null>(null);
  /** Qual popup está aberto — o botão dele diz "Aguardando…". */
  const [pendingProvider, setPendingProvider] =
    useState<FederatedMethod | null>(null);
  /**
   * O `onAuthStateChanged` da `SignInPage` dispara no instante em que o Firebase
   * grava a sessão — dentro do popup do Google, ou do
   * `signInWithEmailAndPassword` — ou seja, enquanto o clique já está chamando o
   * backend. Sem esta guarda toda entrada bate **duas vezes** na rota, e as duas
   * podem terminar diferente (uma entra, a outra derruba a sessão da primeira).
   */
  const entryInFlight = useRef(false);
  const { setAuth } = useAuthStore();
  const navigate = useNavigate();

  const handleSuccess = useCallback(
    (
      data: Awaited<ReturnType<typeof authContainer.repository.signIn>>,
      email: string,
    ) => {
      const accessToken = data.accessToken ?? data.tokens?.accessToken ?? '';
      const accountId = data.account?.id ?? data.user?.id ?? '';
      const resolvedEmail = data.account?.email ?? data.user?.email ?? email;
      setAuth({
        accessToken,
        accountId,
        email: resolvedEmail,
        roles: extractRoles(data),
      });
      void navigate('/');
    },
    [setAuth, navigate],
  );

  /**
   * O login no provedor já deixou uma sessão Firebase local e persistida; se o
   * backend recusou, ela é órfã — sem derrubá-la, o `onAuthStateChanged` da
   * `SignInPage` tentaria reconciliá-la sozinho no próximo carregamento,
   * repetindo o mesmo erro sem o usuário ter clicado em nada, e prendendo-o na
   * conta Google errada (google-sign-in §4.3, passo 5b). Só derruba quando o
   * backend de fato respondeu: queda de rede não é motivo para deslogar.
   */
  const handleError = useCallback(
    async (
      err: unknown,
      fallback: string,
      options?: { fromFederatedPopup?: boolean },
    ) => {
      const code = extractApiErrorCode(err) ?? '';

      // Sem conta nenhuma, o uid que o popup acabou de criar não serve a ninguém:
      // este painel não tem cadastro, então ninguém vai reivindicá-lo depois.
      // Apagar aqui é a única janela em que ainda temos a sessão recente que o
      // `delete()` exige — e é o que impede o órfão de nascer (D4').
      //
      // Duas guardas, e as duas são necessárias: só o popup (no caminho por senha
      // o uid é anterior ao clique) e só um uid recém-nascido — o vínculo
      // automático do Firebase faz o popup cair num uid antigo.
      if (
        options?.fromFederatedPopup &&
        NO_ACCOUNT_CODES.has(code) &&
        firebaseAuth.currentUser &&
        isFreshFederatedUid(firebaseAuth.currentUser)
      ) {
        try {
          await firebaseAuth.currentUser.delete();
        } catch {
          // Falhou (rede, sessão não-recente): a sessão ainda é derrubada abaixo.
          // Órfão a mais é higiene pendente, não bloqueio de quem está na tela.
        }
      }

      // Esperado, não solto: a mensagem abaixo pede uma nova entrada, e a
      // `SignInPage` entra sozinha se ainda achar sessão Firebase viva.
      if (isApiError(err) && !KEEPS_SESSION.has(code)) {
        await authContainer.repository.clearAuth();
      }

      const refusal = REFUSAL_MESSAGES[code];
      if (refusal) {
        setError(refusal);
        const panel = KEEPS_SESSION.has(code) ? accountPanelUrl() : null;
        if (panel) {
          setErrorAction({ label: 'Ir para o Painel de Conta', href: panel });
        }
        return;
      }
      setError(
        firebaseErrorMessage(err) ??
          (err instanceof Error ? err.message : fallback),
      );
    },
    [],
  );

  const signIn = async (input: SignInInput) => {
    setIsLoading(true);
    setError(null);
    setErrorAction(null);
    entryInFlight.current = true;
    try {
      const data = await authContainer.repository.signIn(input);
      handleSuccess(data, input.email);
    } catch (err) {
      // 403 PASSWORD_CHANGE_REQUIRED (senha temporária): leva a temporária para
      // a tela de troca obrigatória, que reautentica e faz o re-login silencioso.
      if (extractApiErrorCode(err) === 'PASSWORD_CHANGE_REQUIRED') {
        void navigate('/change-password-required', {
          state: { email: input.email, tempPassword: input.password },
        });
        return;
      }
      await handleError(err, 'Failed to sign in');
    } finally {
      entryInFlight.current = false;
      setIsLoading(false);
    }
  };

  const signInWithToken = useCallback(
    async (firebaseToken: string) => {
      // Reconciliação de sessão preexistente. Se há uma entrada explícita em
      // curso, ela é quem manda — este caminho só existe para o carregamento de
      // página com sessão Firebase já gravada.
      if (entryInFlight.current) return;
      setIsLoading(true);
      setError(null);
      setErrorAction(null);
      try {
        const data =
          await authContainer.repository.signInWithToken(firebaseToken);
        handleSuccess(data, '');
      } catch (err) {
        // `PASSWORD_CHANGE_REQUIRED` segue para `handleError` de propósito: sem
        // a temporária não há o que reautenticar, e é ele que derruba a sessão
        // — sem isso este mesmo caminho redispararia a cada carregamento.
        await handleError(err, 'Failed to sign in');
      } finally {
        setIsLoading(false);
      }
    },
    [handleSuccess, handleError],
  );

  const signInFederated = useCallback(
    async (method: FederatedMethod) => {
      setIsLoading(true);
      setPendingProvider(method);
      setError(null);
      setErrorAction(null);
      entryInFlight.current = true;
      try {
        const data =
          method === 'google.com'
            ? await authContainer.repository.signInWithGoogle()
            : await authContainer.repository.signInWithMicrosoft();
        handleSuccess(data, '');
      } catch (err) {
        const code = firebaseErrorCode(err);
        // Desistência do popup: volta o formulário ao normal, sem mensagem.
        if (CANCELLED_POPUP_CODES.has(code)) return;
        // Só a Microsoft chega aqui: o Google o Firebase anexa ao uid que já
        // existe (verificado em `primum-core-dev`, 2026-09-03).
        if (code === ACCOUNT_EXISTS_CODE) {
          setError(ACCOUNT_EXISTS_MESSAGE);
          const panel = accountPanelUrl();
          if (panel) {
            setErrorAction({ label: 'Ir para o Painel de Conta', href: panel });
          }
          return;
        }
        await handleError(err, 'Failed to sign in', {
          fromFederatedPopup: true,
        });
      } finally {
        entryInFlight.current = false;
        setPendingProvider(null);
        setIsLoading(false);
      }
    },
    [handleSuccess, handleError],
  );

  const signInWithGoogle = useCallback(
    () => signInFederated('google.com'),
    [signInFederated],
  );

  const signInWithMicrosoft = useCallback(
    () => signInFederated('microsoft.com'),
    [signInFederated],
  );

  return {
    signIn,
    signInWithToken,
    signInWithGoogle,
    signInWithMicrosoft,
    isLoading,
    pendingProvider,
    error,
    errorAction,
  };
}
